<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useLibraryStore } from './library.store'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { copyCollection } from './api'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'
import { useContentAccessStore } from '@/content_access/content_access.store'
import { storeToRefs } from 'pinia'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import InputText from 'primevue/inputtext'
import { LibraryFolder } from './library_types'
import ProgressSpinner from 'primevue/progressspinner'
const analytics = getAnalyticsTracker()
const router = useRouter()
const libraryStore = useLibraryStore()
const userStore = useUserStore()
const contentAccessStore = useContentAccessStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const visibleRef = ref(props.modelValue)
const loading = ref(false)
const copyOfFolderName = ref('')
const copyError = ref('')
const copySuccess = ref(false)
const copiedFolder = ref<LibraryFolder>({} as LibraryFolder)
const { maxFoldersReached, libraryQuotas } = storeToRefs(contentAccessStore)
const { folderCount, folders, currentFolder } = storeToRefs(libraryStore)

const isConflictingName = computed(() => {
  if (!copyOfFolderName.value) return false
  return folders.value.some(folder => folder.name === copyOfFolderName.value)
})

watch(currentFolder, () => {
  copyOfFolderName.value = `Copy of ${currentFolder.value.name}`
})

async function copyFolder() {
  loading.value = true
  copyError.value = ''
  copySuccess.value = false

  try {
    const response = await copyCollection(currentFolder.value.uuid, copyOfFolderName.value) as LibraryFolder
    copiedFolder.value = response
    copySuccess.value = true

    analytics.sendEvent('Copied Student Folder', {
      new_folder_uuid: copiedFolder.value.uuid,
      new_folder_owner: copiedFolder.value.owner
    }) // to be deprecated
    analytics.sendEvent('Copied Folder', {
      new_folder_uuid: copiedFolder.value.uuid,
      new_folder_owner: copiedFolder.value.owner
    })
  } catch (err: any) {
    copyError.value = err.message || 'An unknown error occurred'
    analytics.sendEvent('Error Copying Folder', { message: copyError.value })
    analytics.sendEvent('Error Copying Student', { message: copyError.value }) // to be deprecated
  } finally {
    loading.value = false
  }
}

function closeModal() {
  visibleRef.value = false
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  () => {
    visibleRef.value = props.modelValue
  }
)

watch(
  () => visibleRef.value,
  () => {
    emit('update:modelValue', visibleRef.value)
  }
)

function navigateToLibrary() {
  const url = router.resolve({
    name: 'LibraryFolder',
    params: { uuid: copiedFolder.value.uuid }
  }).href
  window.open(url, '_blank')
}

watch(() => visibleRef.value, async (newVal) => {
  if (newVal) {
    await libraryStore.fetchFolders(userStore.userId)
    if (copyOfFolderName.value && maxFoldersReached.value) {
      analytics.sendEvent('Content Access Blocked', {
        blocked_action: 'Add Folder',
        blocking_quota: 'max_folders',
        blocking_count: folderCount.value,
        blocking_quota_value: libraryQuotas.value.maxFolders
      })
    }
  }
})
</script>
<template>
  <GbDialog
    dialogId="copy-folder-modal"
    v-model:visible="visibleRef"
    header="Copy to Your Library"
    :footer-buttons="true"
    @hide="closeModal"
  >
    <template #bodyContent>
      <div class="flex flex-col gap-4">
        <GbInlineMessage v-if="maxFoldersReached" gbTheme="red">
          <p class="font-bold">Maximum folders reached.</p>
          <p>You have reached the maximum number of folders. You must delete or archive an existing folder to add a new one.</p>
        </GbInlineMessage>

        <template v-if="!loading && !copySuccess && !maxFoldersReached">
          <div class="field">
            <label for="copy-of-folder-name" class="font-medium block mb-2">Folder Name</label>
            <InputText
              v-model="copyOfFolderName"
              id="copy-of-folder-name"
              type="text"
              placeholder="Folder name"
              :invalid="isConflictingName || !copyOfFolderName"
              class="w-full fs-exclude"
            />
            <GbInlineMessage v-if="isConflictingName" gbTheme="red">
              <p class="fs-exclude">A folder named "{{ copyOfFolderName }}" already exists in Your Library.</p>
            </GbInlineMessage>
            <GbInlineMessage v-if="!copyOfFolderName" gbTheme="red">
              <p>Name is required</p>
            </GbInlineMessage>
          </div>
          <p>
            A <span class="font-bold">copy</span> of this folder will be added to Your Library with the above name, along with copies of the folder's goals, strategies, and present levels.
          </p>
          <GbInlineMessage gbTheme="primary">
            <p class="font-bold">Note:</p>
            <p>Changes to the original folder will NOT be reflected in the copied version, and vice versa.</p>
          </GbInlineMessage>
        </template>

        <div v-if="loading">
          <ProgressSpinner strokeWidth="8" class="h-5 w-5 m-0 stroke-primary-400" />
          <span class="ml-2 fs-exclude">Copying and adding {{ copyOfFolderName }} to Your Library...</span>
        </div>

        <GbInlineMessage v-if="copyError" gbTheme="red">
          <p>There was an error copying to Your Library. If the problem persists, contact support and include the following information:</p>
          <p>{{ copyError }}</p>
        </GbInlineMessage>

        <GbInlineMessage v-if="copySuccess" gbTheme="green">
          <p class="fs-exclude">{{ copiedFolder.name }} was successfully copied to Your Library!</p>
        </GbInlineMessage>
      </div>
    </template>

    <template #footerButtonLeft>
      <GbButton
        v-if="!copySuccess"
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        @click="closeModal"
      />
    </template>

    <template #footerButtonRight>
      <GbButton
        v-if="!copySuccess"
        type="button"
        gbTheme="bluePrimary"
        label="Copy"
        :disabled="isConflictingName || !copyOfFolderName || maxFoldersReached || loading"
        @click="copyFolder"
      />
      <GbButton
        v-if="copySuccess"
        type="button"
        gbTheme="bluePrimary"
        label="View in Your Library"
        @click="navigateToLibrary"
      />
    </template>
  </GbDialog>
</template>
